import { useCallback, useState } from "react";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ACTIVE, STATUS_HEX, ageFlag, heldFromStatus, fmtTime } from "@/lib/status";
import AdvanceButton from "@/components/AdvanceButton";
import { AgeBadge, PriorityBadge, TypeBadge } from "@/components/StatusBadge";

const priorityRank = { Urgent: 0, Normal: 1, Low: 2 };

function sortByPriority(items) {
  return [...items].sort((first, second) => {
    const priority =
      (priorityRank[first.priority] ?? 1) - (priorityRank[second.priority] ?? 1);

    if (priority !== 0) {
      return priority;
    }

    return new Date(first.date_submitted) - new Date(second.date_submitted);
  });
}

function KioskCard({ rework, onUpdated }) {
  const flag = ageFlag(rework);
  const isOnHold = rework.status === "On Hold";
  const heldFrom = heldFromStatus(rework);

  return (
    <article
      data-testid={`kiosk-board-card-${rework.id}`}
      className={`space-y-5 border bg-white p-6 shadow-sm transition-transform duration-150 active:scale-[0.98] ${
        isOnHold ? "border-4 border-amber-500 bg-amber-50" : "border-[#e6e5df]"
      }`}
    >
      <Link
        to={`/rework/${rework.id}`}
        data-testid={`kiosk-board-details-${rework.id}`}
        className="block rounded-sm outline-none focus-visible:ring-4 focus-visible:ring-[#647265]/30"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mono font-semibold leading-none text-[#232826] text-[clamp(1.75rem,11cqi,4rem)]">
              {rework.job_number}
            </div>
            <div className="mono mt-2 text-[#424e43] text-[clamp(1.1rem,7cqi,2.5rem)]">{rework.part_number}</div>
            {rework.part_name && <div className="font-semibold text-[#232826] leading-tight text-[clamp(1rem,6cqi,2rem)]">{rework.part_name}</div>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <PriorityBadge priority={rework.priority} />
            <TypeBadge type={rework.request_type} />
          </div>
        </div>
        {isOnHold && (
          <div
            data-testid={`kiosk-hold-reason-${rework.id}`}
            className="mt-5 border-l-4 border-amber-600 bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-950"
          >
            <div>ON HOLD — {rework.hold_reason || "Reason not supplied"}</div>
            <div
              data-testid={`kiosk-hold-origin-${rework.id}`}
              className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-amber-800"
            >
              Paused at: {heldFrom || "Not recorded"}
            </div>
          </div>
        )}
      </Link>
      <div className="flex min-h-6 items-center">
        <AgeBadge flag={flag} testid={`kiosk-age-${rework.id}`} />
      </div>
      <AdvanceButton
        rework={rework}
        onDone={onUpdated}
        size="lg"
        testid={`kiosk-advance-${rework.id}`}
        className="!h-20 whitespace-normal px-4 text-base leading-tight tracking-[0.14em]"
      />
    </article>
  );
}

function KioskColumn({ status, rows, onUpdated }) {
  const id = status.replace(/\s+/g, "-").toLowerCase();

  return (
    <section
      data-testid={`kiosk-board-column-${id}`}
      className="flex min-w-0 flex-col border border-[#e6e5df] bg-[#efece5] [container-type:inline-size]"
    >
      <header
        className="flex items-center justify-between gap-2 border-b-4 bg-[#f7f5f1] px-4 py-4"
        style={{ borderColor: STATUS_HEX[status] }}
      >
        <h2 className="min-w-0 truncate text-base font-extrabold uppercase tracking-[0.12em] text-[#232826] xl:text-lg">
          {status}
        </h2>
        <span
          data-testid={`kiosk-board-count-${id}`}
          className="num min-w-10 shrink-0 bg-white px-3 py-1 text-center text-xl text-[#424e43]"
        >
          {rows.length}
        </span>
      </header>
      <div className="flex-1 space-y-4 p-4">
        {rows.length === 0 && (
          <div className="py-12 text-center text-base font-medium text-[#a39f98]">Clear</div>
        )}
        {rows.map((rework) => (
          <KioskCard key={rework.id} rework={rework} onUpdated={onUpdated} />
        ))}
      </div>
    </section>
  );
}

export default function KioskBoard() {
  const [rows, setRows] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reworks", { params: { active_only: true } });
      setRows(data);
      setLastUpdated(new Date());
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Failed to refresh board");
    }
  }, []);

  useAutoRefresh(load);

  const onUpdated = (updated) => {
    setRows((currentRows) =>
      currentRows
        .map((rework) => (rework.id === updated.id ? updated : rework))
        .filter((rework) => ACTIVE.includes(rework.status)),
    );
  };

  return (
    <div className="min-h-screen bg-[#f7f5f1] pb-32" data-testid="kiosk-board">
      <header className="border-b border-[#e6e5df] bg-[#f7f5f1] px-5 py-5 sm:px-8">
        <div className="flex items-center justify-between gap-5">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.28em] text-[#647265]">
              MISCO · Factory floor
            </div>
            <h1 className="font-display mt-2 text-3xl font-extrabold text-[#232826] sm:text-4xl">
              Live Rework Board
            </h1>
            <div className="mt-2 flex items-center gap-2 font-mono text-xs text-[#6b6a68]" data-testid="kiosk-live-label">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
              Auto-refreshes every 30s · updated {fmtTime(lastUpdated)}
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="kiosk-board-refresh"
            aria-label="Refresh live rework board"
            className="flex min-h-16 min-w-16 items-center justify-center border border-[#cfcfc9] bg-white text-[#424e43] transition-colors duration-150 hover:bg-[#efece5] active:scale-[0.96]"
          >
            <RefreshCw size={26} aria-hidden="true" />
          </button>
        </div>
        <div
          data-testid="kiosk-refresh-progress"
          className="mt-5 h-1 overflow-hidden bg-[#e6e5df]"
        >
          <div key={lastUpdated.getTime()} className="kiosk-refresh-progress h-full bg-[#647265]" />
        </div>
      </header>
      <div className="px-5 py-6 sm:px-8">
        <div className="grid items-start gap-5 [grid-template-columns:repeat(auto-fit,minmax(230px,1fr))]">
          {ACTIVE.map((status) => (
            <KioskColumn
              key={status}
              status={status}
              rows={sortByPriority(rows.filter((rework) => rework.status === status))}
              onUpdated={onUpdated}
            />
          ))}
        </div>
      </div>
    </div>
  );
}